import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Edit, Eye, Pill, RefreshCw, Download, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getProducts, getProductForms, getProductStatuses, downloadExport, deleteProduct, uploadProductsCSV, downloadProductTemplate } from '../../config/api';
import { Upload as ImportIcon } from 'lucide-react';
import CSVImportModal from '../../components/CSVImportModal';
import { useAuth } from '../../contexts/AuthContext';

// Controlled Search Input Component with Focus Preservation
const SearchInput = React.memo(({ value, onChange, placeholder }) => {
  const inputRef = React.useRef(null);
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  // Sync with external value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);
  
  // Handle changes
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    setCursorPosition(e.target.selectionStart);
    onChange(e);
  }, [onChange]);
  
  // Handle focus events
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);
  
  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);
  
  // Track cursor position on selection
  const handleSelect = useCallback((e) => {
    setCursorPosition(e.target.selectionStart);
  }, []);
  
  // Preserve focus and cursor position
  useEffect(() => {
    if (inputRef.current && isFocused) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Restore cursor position if it's within bounds
          const pos = Math.min(cursorPosition, inputRef.current.value.length);
          inputRef.current.setSelectionRange(pos, pos);
        }
      });
    }
  });
  
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelect}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        autoComplete="off"
        spellCheck="false"
      />
    </div>
  );
});

const Products = () => {
  
  const navigate = useNavigate();
  const [showImport, setShowImport] = useState(false);
  const { user } = useAuth();
  
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterWac, setFilterWac] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('productName');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Export products to Excel
  const exportToExcel = async () => {
    try {
      await downloadExport('/api/products/export', `products-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Products exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export products to Excel');
    }
  };

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);
  


  // Fetch products with optimized cache settings
  const { 
    data: response, 
    isLoading, 
    error, 
    refetch,
    isFetching 
  } = useQuery({
          queryKey: ['products', currentPage, filterStatus, filterWac, sortField, sortDirection], // Removed debouncedSearchTerm
    queryFn: async () => {
      const params = { 
        page: currentPage, 
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        status: filterStatus === 'ALL' ? undefined : filterStatus,
        sortBy: sortField,
        sortOrder: sortDirection
      };
      try {
        const result = await getProducts(params);
        return result;
      } catch (error) {
        console.error('🔍 API call failed:', error);
        throw error;
      }
    },
    enabled: !!user,
    retry: false,
    staleTime: 1000, // Refetch after 1 second
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnMount: true, // Refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchInterval: false, // Don't auto-refetch
    refetchOnReconnect: false // Don't refetch on reconnect
  });

  // Extract products and pagination data
  const products = response?.products || [];
  const pagination = response?.pagination || { total: 0, pages: 0 };
  
  // Trigger search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm && refetch) {
      refetch();
    }
  }, [debouncedSearchTerm, searchTerm, refetch]);
  



  // Handlers

  const handleDelete = useCallback(async (product) => {
    if (!window.confirm(`Delete product "${product.name}"? It will be moved to Trash (an admin can restore or purge it).`)) return;
    try {
      await deleteProduct(product.id);
      toast.success(`Deleted ${product.name}`);
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Failed to delete product');
    }
  }, [refetch]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  }, []);
  
  // Stable search handler that won't cause re-renders
  const stableSearchHandler = useCallback((e) => {
    handleSearch(e);
  }, [handleSearch]);

  const handleFilterChange = useCallback((filterType, value) => {
    switch (filterType) {
      case 'status':
        setFilterStatus(value);
        break;
      case 'wac':
        setFilterWac(value);
        break;
      default:
        break;
    }
    setCurrentPage(1); // Reset to first page on filter change
    
    // Force refetch when filters change
    setTimeout(() => {
      if (refetch) {
        refetch();
      }
    }, 100);
  }, [refetch]);
  
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  }, [sortField]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    refetch();
    toast.success('Data refreshed!');
  };

  // Filter products based on search and filters - optimized with useMemo
  const filteredProducts = useMemo(() => {
    
    const filtered = products.filter(product => {
      // Enhanced search with better field handling
      const matchesSearch = !debouncedSearchTerm || (() => {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const productName = product.name || '';
        const productSku = product.sku || '';
        const productUpc = product.upc || '';
        
        return productName.toLowerCase().includes(searchLower) ||
               productSku.toLowerCase().includes(searchLower) ||
               (productUpc && productUpc.toLowerCase().includes(searchLower));
      })();
      
      const matchesStatus = filterStatus === 'ALL' || 
        (filterStatus === 'ACTIVE' && product.isActive === true) ||
        (filterStatus === 'INACTIVE' && product.isActive === false);
      
      const matchesWac = filterWac === 'ALL' ||
        (filterWac === 'HAS_WAC' && product.wac !== null) ||
        (filterWac === 'NO_WAC' && product.wac === null);
      
      const passes = matchesSearch && matchesStatus && matchesWac;
      
      if (!passes && products.length > 0) {
      }
      
      return passes;
    });
    
    
    return filtered;
  }, [products, debouncedSearchTerm, filterStatus, filterWac]);


  // Redirect if not authenticated
  if (!user) {
    navigate('/app/login');
    return null;
  }
  

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <RefreshCw className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">
          <Pill size={48} className="mx-auto mb-2" />
          <p className="text-lg font-semibold">Error loading products</p>
          <p className="text-sm">{error.message}</p>
        </div>
        <button
          onClick={() => refetch()}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          title="Try Again"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pharmaceutical Products</h1>
          <p className="text-gray-600 mt-1">
            {pagination.total.toLocaleString()} products • Page {currentPage} of {pagination.pages}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
            title="Export Excel"
          >
            <Download size={20} />
            Export Excel
          </button>
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50"
            title="Import products from CSV"
          >
            <ImportIcon size={20} />
            Import CSV
          </button>
          {showImport && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <CSVImportModal
                title="Import Products"
                entityLabel="products"
                uploadFn={uploadProductsCSV}
                templateFn={downloadProductTemplate}
                note="Products are matched by SKU (or NDC-11) — existing are updated, new are created."
                onUploadComplete={() => { setShowImport(false); refetch(); }}
                onClose={() => setShowImport(false)}
              />
            </div>
          )}
          <button
            onClick={() => navigate('/app/products/new')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
            title="Add Product"
          >
            <Plus size={20} />
            Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Products
              {searchTerm !== debouncedSearchTerm && (
                <span className="ml-2 text-sm text-blue-600">(Searching...)</span>
              )}
            </label>
            <SearchInput
              key="stable-search-input"
              value={searchTerm}
              onChange={stableSearchHandler}
              placeholder="Search by name, generic, NDC, or external ID..."
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          {/* WAC Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WAC</label>
            <select
              value={filterWac}
              onChange={(e) => handleFilterChange('wac', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="ALL">All Products</option>
              <option value="HAS_WAC">Has WAC</option>
              <option value="NO_WAC">No WAC</option>
            </select>
          </div>
        </div>


        
        {/* Sort Controls */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <div className="flex gap-2 flex-wrap">
            {[
              { field: 'name', label: 'Product Name' },
              { field: 'sku', label: 'SKU' },
              { field: 'upc', label: 'UPC' },
              { field: 'uom', label: 'Unit' },
              { field: 'isActive', label: 'Status' },
              { field: 'wac', label: 'WAC' },
              { field: 'createdAt', label: 'Created Date' }
            ].map(({ field, label }) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                title={`Sort by ${label}`}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  sortField === field
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {label}
                {sortField === field && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Product Name & Details
                    {sortField === 'name' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('upc')}
                >
                  <div className="flex items-center gap-1">
                    UPC Code
                    {sortField === 'upc' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sku')}
                >
                  <div className="flex items-center gap-1">
                    SKU
                    {sortField === 'sku' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('uom')}
                >
                  <div className="flex items-center gap-1">
                    Unit
                    {sortField === 'uom' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isActive')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'isActive' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('wac')}
                >
                  <div className="flex items-center gap-1">
                    WAC Price
                    {sortField === 'wac' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900 break-words">
                        {product.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        NDC: {product.ndc11 || product.productNdc || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        ID: {product.id?.slice(0, 8) || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {product.upc || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {product.sku || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="font-medium">
                        {product.uom || 'EA'}
                      </div>
                      <div className="text-gray-500 text-xs">
                        Each
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      product.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {product.wac ? (
                        <span className="font-medium text-green-600">
                          ${parseFloat(product.wac).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">No WAC set</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/app/products/${product.id}`)}
                        className="text-primary-600 hover:text-primary-900 p-1"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => {
                          navigate(`/app/products/${product.id}/edit`);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Pill size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">No products found</p>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'ALL' || filterWac !== 'ALL'
                ? 'Try adjusting your search or filters'
                : 'No products available in the database'
              }
            </p>
            {(searchTerm || filterStatus !== 'ALL' || filterWac !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('ALL');
                  setFilterWac('ALL');
                  setCurrentPage(1);
                }}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
                title="Clear All Filters"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6">
          {/* Page Info */}
          <div className="text-center text-sm text-gray-600 mb-4">
            Showing page {currentPage} of {pagination.pages} ({pagination.total} total products)
          </div>
          
          {/* Pagination Controls */}
          <div className="flex justify-center">
            <nav className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First Page"
            >
              ««
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous Page"
            >
              Previous
            </button>
            
            {(() => {
              const totalPages = pagination.pages;
              const current = currentPage;
              const maxVisible = 5;
              
              let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
              let endPage = Math.min(totalPages, startPage + maxVisible - 1);
              
              // Adjust start page if we're near the end
              if (endPage - startPage + 1 < maxVisible) {
                startPage = Math.max(1, endPage - maxVisible + 1);
              }
              
              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }
              
              return pages.map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  title={`Page ${page}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ));
            })()}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next Page"
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(pagination.pages)}
              disabled={currentPage === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last Page"
            >
              »»
            </button>
            </nav>
          </div>
        </div>
      )}

    </div>
  );
};

export default Products;
